import * as yup from 'yup';
import {
  AutomationStatus,
  AutomationTriggerEvent,
  CampaignMedium,
  ServiceCategory,
} from '@prisma/client';
import { MarketingEmailTemplateWithSource } from '.';
import { isEmailTemplateValid } from '../../helpers/marketing';

export type AutomationRecipeId =
  | 'healing-check-in'
  | 'piercing-downsize-reminder'
  | 'tattoo-rebooking-nudge'
  | 'pmu-perfecting-session';

export type AutomationDelayUnit = 'MINUTES' | 'HOURS' | 'DAYS' | 'WEEKS';

export interface AutomationDelay {
  timing: 'IMMEDIATE' | 'AFTER';
  amount: string;
  unit: AutomationDelayUnit;
}

export interface AutomationFormValues {
  recipeId: AutomationRecipeId | null;
  name: string;
  channel: CampaignMedium;
  triggerEvent: AutomationTriggerEvent;
  delay: AutomationDelay;
  serviceCategories: ServiceCategory[];
  emailAddressId: string;
  templateId: number | null;
}

/// Behavioural fields; the API owns these and validates automations against them
export interface AutomationRecipeConfig {
  id: AutomationRecipeId;
  triggerEvent: AutomationTriggerEvent;
  delaySeconds: number;
  serviceCategories: ServiceCategory[];
  channel: CampaignMedium;
}

export interface AutomationRecipeDisplay {
  title: string;
  description: string;
}

export type AutomationRecipe = AutomationRecipeConfig & AutomationRecipeDisplay;

export interface CreateAutomationRequest {
  marketingBusinessId: string;
  recipeId?: AutomationRecipeId;
  name: string;
  channel: CampaignMedium;
  triggerEvent: AutomationTriggerEvent;
  delaySeconds: number;
  serviceCategories: ServiceCategory[];
  emailAddressId?: string;
  templateId: number;
}

export interface RecipeTemplateRequest {
  marketingBusinessId: string;
  recipeId: AutomationRecipeId;
}

export interface AutomationListItem {
  id: string;
  name: string;
  recipeId: string | null;
  triggerSummary: string;
  status: AutomationStatus;
  customersReached: number | null;
}

export interface AutomationActionHandlers {
  onPauseAutomation: (automationId: string) => void;
  onResumeAutomation: (automationId: string) => void;
  onDeleteAutomation: (automationId: string) => void;
}

const NAME_ERROR = 'Please enter a name for your automation';
const DELAY_AMOUNT_ERROR = 'Please enter a delay of at least 1';
const TEMPLATE_ERROR = 'Please select a valid template';

export const getAutomationFormSchema = (
  emailTemplates: MarketingEmailTemplateWithSource[],
  recipeTemplateId: number | null = null,
) =>
  yup.object({
    name: yup.string().trim().required(NAME_ERROR),
    channel: yup.mixed<CampaignMedium>().oneOf(['EMAIL', 'SMS']).required(),
    triggerEvent: yup
      .mixed<AutomationTriggerEvent>()
      .oneOf(Object.values(AutomationTriggerEvent))
      .required(),
    delay: yup.object({
      timing: yup.mixed().oneOf(['IMMEDIATE', 'AFTER']).required(),
      amount: yup.string().when('timing', {
        is: 'AFTER',
        then: schema =>
          schema
            .required(DELAY_AMOUNT_ERROR)
            .matches(/^[1-9]\d*$/, DELAY_AMOUNT_ERROR),
      }),
      unit: yup.mixed().oneOf(['MINUTES', 'HOURS', 'DAYS', 'WEEKS']).required(),
    }),
    serviceCategories: yup
      .array(yup.mixed().oneOf(Object.values(ServiceCategory)))
      .required(),
    emailAddressId: yup.string(),
    templateId: yup
      .number()
      .nullable()
      .required(TEMPLATE_ERROR)
      .test({
        message: TEMPLATE_ERROR,
        test: (templateId, context) => {
          if (context.parent.channel !== 'EMAIL') return true;
          // Recipe templates are cloned from curated project templates and kept out of the library
          if (recipeTemplateId !== null && templateId === recipeTemplateId)
            return true;
          const template = emailTemplates.find(t => t.id === templateId);
          return !!template && isEmailTemplateValid(template.source);
        },
      }),
  });
